import React from 'react';
import { motion } from 'framer-motion';
import { Flame, Beef, Wheat, Droplet, HeartPulse, Info } from 'lucide-react';
import { MACRO_META, healthLabel } from './trackingConstants';

/* Anneau circulaire animé (SVG) - basé sur un viewBox proportionnel (et non des px fixes) pour
   remplir naturellement toute la largeur disponible de son conteneur (max-width posé par l'appelant) */
const Ring = ({ pct, stroke = 9, color, children, className = '' }) => {
  const vb = 100;
  const r = (vb - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const clamped = Math.min(pct || 0, 100);
  const offset = circ * (1 - clamped / 100);
  const over = (pct || 0) > 100;
  return (
    <div className={`relative w-full aspect-square ${className}`}>
      <svg viewBox={`0 0 ${vb} ${vb}`} className="-rotate-90 w-full h-full">
        <circle cx={vb / 2} cy={vb / 2} r={r} fill="none" stroke="rgb(var(--c-white) / 0.08)" strokeWidth={stroke} />
        <motion.circle
          cx={vb / 2} cy={vb / 2} r={r} fill="none"
          stroke={over ? '#F97316' : color} strokeWidth={stroke} strokeLinecap="round"
          strokeDasharray={circ}
          initial={{ strokeDashoffset: circ }}
          animate={{ strokeDashoffset: offset }}
          transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
          style={{ filter: `drop-shadow(0 0 10px ${color}90) drop-shadow(0 0 22px ${color}50)` }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">{children}</div>
    </div>
  );
};

const MacroBar = ({ mkey, consumed, goal, pct, en }) => {
  const meta = MACRO_META[mkey];
  const icon = { protein: Beef, carbs: Wheat, fat: Droplet }[mkey];
  const Icon = icon;
  return (
    <div className="rounded-2xl bg-white/[0.035] backdrop-blur-xl border border-white/10 shadow-[0_0_30px_-12px_rgba(217,70,239,0.25)] p-4" data-testid={`macro-card-${mkey}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4" style={{ color: meta.color }} />
        <span className="text-sm font-semibold text-zinc-300">{en ? meta.en : meta.fr}</span>
      </div>
      <div className="flex items-end gap-1.5">
        <span className="text-2xl font-extrabold text-white tabular-nums">{Math.round(consumed)}</span>
        <span className="text-sm text-zinc-500 mb-0.5">/ {Math.round(goal)} g</span>
      </div>
      <div className="mt-2 h-2 rounded-full bg-white/8 overflow-hidden">
        <motion.div
          className="h-full rounded-full"
          style={{ background: pct > 100 ? '#F97316' : meta.color }}
          initial={{ width: 0 }} animate={{ width: `${Math.min(pct, 100)}%` }}
          transition={{ duration: 1, ease: 'easeOut' }}
        />
      </div>
    </div>
  );
};

export const ProgressRings = ({ summary, en, onScoreInfo, activityKcal = 0 }) => {
  const { totals, goals, percent, health_score } = summary;
  const hl = healthLabel(health_score, en);
  const calColor = MACRO_META.calories.color;
  const adjustedGoal = goals.calories + (activityKcal || 0);
  const remainingCal = Math.round(adjustedGoal - totals.calories);
  const ringPct = adjustedGoal ? (totals.calories / adjustedGoal) * 100 : 0;

  return (
    <div className="grid lg:grid-cols-[380px_1fr] gap-6 items-start" data-testid="progress-rings">
      {/* Carte calories principale : anneau plein-largeur (remplit tout l'espace dispo) avec
         restant ET objectif total tous deux clairement visibles (pas juste le restant en gros
         et le total en minuscule) */}
      <div className="relative overflow-hidden bg-gradient-to-br from-zinc-900/90 to-black/90 backdrop-blur-xl border border-white/10 shadow-[0_0_35px_-10px_rgba(217,70,239,0.3)] rounded-3xl p-5 sm:p-6 flex flex-col items-center" data-testid="calorie-ring-container">
        <div className="pointer-events-none absolute -top-16 -right-16 w-56 h-56 rounded-full blur-[80px] opacity-40" style={{ background: calColor }} aria-hidden="true" />
        <div className="pointer-events-none absolute -bottom-20 -left-16 w-56 h-56 rounded-full blur-[90px] opacity-25 bg-purple-600" aria-hidden="true" />
        <div className="relative grid grid-cols-3 gap-3 w-full mb-5 text-center">
          <div data-testid="calories-consumed">
            <p className="text-lg sm:text-xl font-extrabold text-white tabular-nums">{Math.round(totals.calories)}</p>
            <p className="text-[11px] text-zinc-500">{en ? 'Eaten' : 'Mangées'}</p>
          </div>
          <div data-testid="calories-burned">
            <p className="text-lg sm:text-xl font-extrabold text-orange-400 tabular-nums">{Math.round(activityKcal || 0)}</p>
            <p className="text-[11px] text-zinc-500">{en ? 'Burned' : 'Brûlées'}</p>
          </div>
          <div data-testid="calorie-remaining-value">
            <p className={`text-lg sm:text-xl font-extrabold tabular-nums ${remainingCal >= 0 ? 'text-white' : 'text-orange-400'}`}>
              {remainingCal >= 0 ? Math.round(remainingCal) : `+${Math.abs(remainingCal)}`}
            </p>
            <p className="text-[11px] text-zinc-500">{en ? (remainingCal >= 0 ? 'Left' : 'Over') : (remainingCal >= 0 ? 'Restantes' : 'En trop')}</p>
          </div>
        </div>
        <Ring pct={ringPct} stroke={9} color={calColor} className="max-w-[260px] sm:max-w-[320px]">
          <Flame className="w-6 h-6 mb-1.5" style={{ color: calColor }} />
          <div className="text-4xl sm:text-5xl font-black text-white tabular-nums leading-none" data-testid="calorie-remaining-hero">
            {Math.abs(remainingCal)}
          </div>
          <div className="text-xs sm:text-sm text-zinc-400 font-semibold mt-1.5">{en ? (remainingCal >= 0 ? 'kcal left' : 'kcal over') : (remainingCal >= 0 ? 'kcal restantes' : 'kcal en trop')}</div>
          <div className="mt-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-sm sm:text-base font-bold text-zinc-200 tabular-nums" data-testid="calorie-goal-total">
            {en ? 'of' : 'sur'} {Math.round(adjustedGoal)} kcal
          </div>
        </Ring>
      </div>

      {/* Macros + score santé */}
      <div className="grid sm:grid-cols-2 gap-4 w-full">
        <MacroBar mkey="protein" consumed={totals.protein} goal={goals.protein} pct={percent.protein} en={en} />
        <MacroBar mkey="carbs" consumed={totals.carbs} goal={goals.carbs} pct={percent.carbs} en={en} />
        <MacroBar mkey="fat" consumed={totals.fat} goal={goals.fat} pct={percent.fat} en={en} />
        {/* Score santé */}
        <div className="rounded-2xl bg-white/[0.035] backdrop-blur-xl border border-white/10 shadow-[0_0_30px_-12px_rgba(217,70,239,0.25)] p-4" data-testid="health-score-card">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <HeartPulse className="w-4 h-4" style={{ color: hl.color }} />
              <span className="text-sm font-semibold text-zinc-300">{en ? 'Health score' : 'Score santé'}</span>
            </div>
            {onScoreInfo && (
              <button onClick={onScoreInfo} className="p-1 rounded-md text-zinc-500 hover:text-primary hover:bg-white/5 transition-colors" data-testid="health-score-info" title={en ? 'How is it calculated?' : 'Comment est-il calculé ?'}>
                <Info className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <div className="flex items-end gap-1.5">
            <span className="text-2xl font-extrabold text-white tabular-nums">{health_score ?? '—'}</span>
            {health_score != null && <span className="text-sm text-zinc-500 mb-0.5">/ 100</span>}
          </div>
          <div className="mt-2 h-2 rounded-full bg-white/8 overflow-hidden">
            <motion.div
              className="h-full rounded-full"
              style={{ background: hl.color }}
              initial={{ width: 0 }} animate={{ width: `${health_score || 0}%` }}
              transition={{ duration: 1, ease: 'easeOut' }}
            />
          </div>
          <p className="text-xs font-semibold mt-1.5" style={{ color: hl.color }}>{hl.txt}</p>
        </div>
      </div>
    </div>
  );
};

export default ProgressRings;
